import { formatTableCellValue, formatUkDateTime } from '../../../utils/functions';

/** Params set by page-level reporting filters (not per-chart overrides). */
export const CHART_DATE_PARAMS = new Set(['p_date_from', 'p_date_to', 'datefrom', 'dateto']);

export const GLOBAL_DASHBOARD_FILTER_PARAMS = new Set([
    'p_date_from', 'p_date_to', 'datefrom', 'dateto', 'date_from', 'date_to',
    'p_cohort_id', 'cohortid', 'cohort_id',
    'p_upload_batch_id', 'uploadbatchid', 'upload_batch_id',
    'p_organisation_id', 'organisationid', 'organisation_id', 'organizationid', 'organization_id',
]);

export function isChartDateParam(name) {
    if (!name) return false;
    const n = String(name).toLowerCase();
    return CHART_DATE_PARAMS.has(n) || n === 'date_from' || n === 'date_to';
}

export function isGlobalDashboardFilterParam(name) {
    if (!name) return false;
    return GLOBAL_DASHBOARD_FILTER_PARAMS.has(String(name).toLowerCase());
}

/**
 * @param {object} [globalFilters] - { cohortId?, uploadBatchId? }
 */
/** Sum the primary value column (e.g. total_count) for a chart footer total. */
export function computeChartTotal(dataset, vizSpec = {}) {
    if (!dataset?.rows?.length) return null;
    const { columns = [], rows = [] } = dataset;
    const { encoding = {} } = vizSpec;
    const { value } = encoding;
    const valCols = value
        ? (Array.isArray(value) ? value : [value])
        : columns.filter(c => c.type === 'number').map(c => c.key);
    if (valCols.length === 0) return null;

    const key = valCols.find(k => k === 'total_count' || k === 'record_count') ?? valCols[0];
    let sum = 0;
    for (const row of rows) {
        const n = Number(row[key]);
        if (Number.isFinite(n)) sum += n;
    }
    return sum;
}

/** Coerce cohort / upload-batch filter values to a plain id string for the API. */
export function normalizeFilterId(value) {
    if (value == null || value === '') return undefined;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed || undefined;
    }
    if (typeof value === 'object') {
        const id = value.id ?? value.Id;
        if (id != null && id !== '') return String(id);
    }
    return String(value);
}

export function buildChartExecuteFilter(chartDef, dateRange, globalFilters = {}) {
    const overrides = chartDef?.parameterOverrides ?? {};
    const parameters = {};
    for (const [key, value] of Object.entries(overrides)) {
        if (value === undefined || value === null || value === '') continue;
        if (isGlobalDashboardFilterParam(key)) continue;
        parameters[key] = value;
    }

    const cohort = normalizeFilterId(globalFilters.cohortId);
    const batch = normalizeFilterId(globalFilters.uploadBatchId);
    const organisation = normalizeFilterId(globalFilters.organisationId);
    if (cohort) parameters.p_cohort_id = cohort;
    if (batch) parameters.p_upload_batch_id = batch;
    if (organisation) parameters.p_organisation_id = organisation;

    const filter = { parameters };

    if (dateRange?.[0]) {
        filter.dateFrom = dateRange[0].toISOString?.() ?? dateRange[0];
    }
    if (dateRange?.[1]) {
        filter.dateTo = dateRange[1].toISOString?.() ?? dateRange[1];
    }

    return filter;
}

/** Stable signature for chart cache keys and filter-change debouncing (must match execute payload). */
export function buildChartFilterSignature(dateRange, globalFilters = {}, chartDef = {}) {
    return JSON.stringify(buildChartExecuteFilter(chartDef, dateRange, globalFilters));
}

function newChartId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `chart-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Semantic colors for known summary dimensions (RAG, pipeline, OMOP). */
export function colorSchemeForDimension(dimensionFilter) {
    if (dimensionFilter === 'quality') return 'data_quality';
    if (dimensionFilter === 'nhs_validation') return 'nhs_validation';
    if (dimensionFilter === 'data_quality_reason') return 'data_quality_reason';
    if (dimensionFilter === 'processing_status' || dimensionFilter === 'row_outcome') return 'processing';
    if (dimensionFilter === 'omop_import_status') return 'omop';
    return undefined;
}

/** Attach inferred colorScheme when missing (existing dashboards saved before defaults). */
export function enrichVizSpec(viz, dimensionFilter) {
    if (!viz) return viz;
    const colorScheme = viz.colorScheme ?? colorSchemeForDimension(dimensionFilter);
    if (!colorScheme || colorScheme === viz.colorScheme) return viz;
    return { ...viz, colorScheme };
}

export function cloneChart(chart, options = {}) {
    const { titleSuffix = ' (copy)', layoutDelta = { x: 0, y: 2 } } = options;
    const layout = chart.layout ?? { x: 0, y: 0, w: 6, h: 10 };
    return {
        ...chart,
        id: newChartId(),
        title: chart.title ? `${chart.title}${titleSuffix}` : `Chart${titleSuffix}`,
        layout: {
            ...layout,
            x: (layout.x ?? 0) + layoutDelta.x,
            y: (layout.y ?? 0) + layoutDelta.y,
        },
    };
}

export function cloneDashboardForCreate(dashboard, nameSuffix = ' (copy)') {
    const charts = (dashboard.layoutJson?.charts ?? []).map(c => cloneChart(c, { layoutDelta: { x: 0, y: 0 } }));
    return {
        name: `${dashboard.name || 'Dashboard'}${nameSuffix}`,
        description: dashboard.description,
        layoutJson: { ...(dashboard.layoutJson ?? {}), charts },
    };
}

/** Format dataset cell values for charts/tables (UK dates via shared utils). */
export function formatReportingDisplayValue(value, columnType) {
    if (columnType === 'datetime' || value instanceof Date) {
        return formatUkDateTime(value);
    }
    return formatTableCellValue(value);
}
